import { Radio } from '@base-ui/react/radio';
import { RadioGroup } from '@base-ui/react/radio-group';
import { cva, type VariantProps } from 'class-variance-authority';
import * as React from 'react';

import { cn } from '../../lib/cn';

/**
 * A segmented control: two or more mutually exclusive options joined into one
 * bar, where exactly one is always chosen.
 *
 * It is a radio group, not a row of toggle buttons. A toggle can be switched
 * off, leaving nothing chosen, and a screen reader announces it as pressed.
 * Here the reader announces "1 of 3", which is what the control means.
 *
 * Colour comes from the same `button/*` tokens the Button uses, so a segmented
 * control and a button of the same tone always agree.
 */

const segmentedControlVariants = cva(
  'bg-surface-elevated inline-flex w-fit divide-x overflow-hidden border',
  {
    variants: {
      tone: {
        neutral: 'divide-button-neutral-border border-button-neutral-border',
        accent: 'divide-button-accent-border border-button-accent-border',
        warning: 'divide-button-warning-border border-button-warning-border',
        critical: 'divide-button-critical-border border-button-critical-border',
      },
      /** Matches Button: sm 36 px / 14 px text, md 40 / 16, lg 44 / 18. */
      size: {
        sm: 'h-9 rounded-md text-sm',
        md: 'h-10 rounded-[7px] text-base',
        lg: 'h-11 rounded-lg text-lg',
      },
    },
    defaultVariants: { tone: 'neutral', size: 'sm' },
  },
);

const segmentVariants = cva(
  // The ring is drawn inside so it is not clipped by the group's overflow.
  'focus-visible:ring-ring/50 inline-flex cursor-pointer items-center justify-center font-medium whitespace-nowrap transition-colors outline-none select-none focus-visible:ring-3 focus-visible:ring-inset data-disabled:pointer-events-none data-disabled:opacity-50',
  {
    variants: {
      tone: {
        neutral:
          'text-button-neutral-fg data-checked:bg-button-neutral-primary data-checked:text-button-neutral-primary-fg data-unchecked:hover:bg-button-neutral-quiet-hover',
        accent:
          'text-button-accent-fg data-checked:bg-button-accent-primary data-checked:text-button-accent-primary-fg data-unchecked:hover:bg-button-accent-quiet-hover',
        warning:
          'text-button-warning-fg data-checked:bg-button-warning-primary data-checked:text-button-warning-primary-fg data-unchecked:hover:bg-button-warning-quiet-hover',
        critical:
          'text-button-critical-fg data-checked:bg-button-critical-primary data-checked:text-button-critical-primary-fg data-unchecked:hover:bg-button-critical-quiet-hover',
      },
      size: {
        sm: 'px-3',
        md: 'px-3.5',
        lg: 'px-4',
      },
    },
    defaultVariants: { tone: 'neutral', size: 'sm' },
  },
);

type SegmentedControlTone = NonNullable<VariantProps<typeof segmentedControlVariants>['tone']>;
type SegmentedControlSize = NonNullable<VariantProps<typeof segmentedControlVariants>['size']>;

const SegmentedControlContext = React.createContext<{
  tone: SegmentedControlTone;
  size: SegmentedControlSize;
}>({ tone: 'neutral', size: 'sm' });

export interface SegmentedControlProps<Value extends string = string> extends Omit<
  RadioGroup.Props<Value>,
  'className'
> {
  className?: string;
  tone?: SegmentedControlTone;
  size?: SegmentedControlSize;
  /**
   * Give every segment the same width. Use it when the options are comparable
   * in length; leave it off when one label is much longer than the others.
   */
  equalWidths?: boolean;
}

export function SegmentedControl<Value extends string = string>({
  className,
  tone = 'neutral',
  size = 'sm',
  equalWidths = false,
  ...props
}: SegmentedControlProps<Value>) {
  const context = React.useMemo(() => ({ tone, size }), [tone, size]);
  return (
    <SegmentedControlContext.Provider value={context}>
      <RadioGroup
        data-slot="segmented-control"
        data-tone={tone}
        data-size={size}
        className={cn(
          segmentedControlVariants({ tone, size }),
          equalWidths && '*:flex-1',
          className,
        )}
        {...props}
      />
    </SegmentedControlContext.Provider>
  );
}

export interface SegmentedControlItemProps<Value extends string = string> extends Omit<
  Radio.Root.Props<Value>,
  'className'
> {
  className?: string;
}

export function SegmentedControlItem<Value extends string = string>({
  className,
  ...props
}: SegmentedControlItemProps<Value>) {
  const { tone, size } = React.useContext(SegmentedControlContext);
  return (
    <Radio.Root
      data-slot="segmented-control-item"
      className={cn(segmentVariants({ tone, size }), className)}
      {...props}
    />
  );
}
